import React, { useState } from "react";
import { HiX } from "react-icons/hi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaPrint } from "react-icons/fa";
import { format } from "date-fns";
import { PrintLayout } from "./PrintLayout";
import ConfirmActionModal from "./ConfirmDeleteModal";

const ViewPurchaseModal = ({
  isOpen,
  onClose,
  purchase,
  title = "Purchase Details",
  onDelete,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  if (!isOpen || !purchase) return null;
  let parsedItems = [];
    try {
      parsedItems = typeof purchase.items === 'string' 
        ? JSON.parse(purchase.items) 
        : (purchase.items || []);
    } catch (error) {
      console.error("Error parsing items JSON:", error);
      parsedItems = [];
    }

  const handlePrint = () => {
    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    } else {
      console.error("Print layout not found!");
    }

    setTimeout(() => {
      window.print();

      setTimeout(() => {
        if (printLayout) {
          printLayout.style.display = "none";
        }
      }, 500);
    }, 300);
  };
// Signature footer content for printing

const signatureFooter = (
  <div className="w-60 text-center">
    <div className="border-t border-gray-400 pt-3"></div>
    <p className="font-bold text-gray-800 mt-2">
      {purchase.authorizedBy || "N/A"}
    </p>
    <p className="text-sm text-gray-600">
      Authorized By
    </p>
  </div>
);
  const handleDeleteConfirm = (purchaseId) => {
    if (onDelete) {
      onDelete(purchaseId);
    }
    onClose();
  };

  const billInfoContent = (
    <>
      <div>
        <p>
          <strong>Purchase ID:</strong> {purchase.id || "N/A"}
        </p>
        <p>
          <strong>Bill No:</strong> {purchase.billNo || "N/A"}
        </p>
        <p>
          <strong>Purchase Date:</strong>{" "}
          {purchase.purchaseDate
            ? format(new Date(purchase.purchaseDate), "MM/dd/yyyy h:mm a")
            : "N/A"}
        </p>
        <p>
          <strong>Supplier Name:</strong>{" "}
          {purchase.supplier?.supplierName || "N/A"}
        </p>
        <p>
          <strong>Authorized By:</strong>{" "}
          {purchase.authorizedBy || "N/A"}
        </p>  
      </div>
      <div>
        <p>
          <strong>Payment Mode:</strong> {purchase.paymentMode || "N/A"}
        </p>
        <p>
          <strong>Payment Amount:</strong> ৳
          {purchase.paymentAmount ? purchase.paymentAmount.toFixed(2) : "0.00"}
        </p>
        <p>
          <strong>Net Amount:</strong> ৳
          {purchase.netAmount ? purchase.netAmount.toFixed(2) : "0.00"}
        </p>
        {purchase.returnAmount !== null && (
          <p>
            <strong>Return Amount:</strong> ৳{purchase.returnAmount.toFixed(2)}
          </p>
        )}
        {purchase.dueAmount !== null && (
          <p>
            <strong>Due Amount:</strong> ৳{purchase.dueAmount.toFixed(2)}
          </p>
        )}
      </div>
    </>
  );

  const tableContent = (
    <>
      <thead>
        <tr className="text-xs">
          <th>Medicine Name</th>
          <th>Category</th>
          <th>Batch No.</th>
          <th>Expiry Date</th>
          <th>Box Qty</th>
          <th>Available Quantity</th>
          <th>Supplier Price (৳)</th>
          <th>Amount (৳)</th>
        </tr>
      </thead>
      <tbody>
        {parsedItems.length > 0 ? (
          parsedItems.map((item, index) => (
            <tr key={index} className="text-xs">
              <td>{item.medicineName || "N/A"}</td>
              <td>{item.medicineCategory || "N/A"}</td>
              <td>{item.batchNo || "N/A"}</td>
              <td>
                {item.expiryDate
                  ? format(new Date(item.expiryDate), "yyyy-MM-dd")
                  : "N/A"}
              </td>
              <td>{item.boxQty || "0"}</td>
              <td>{item.availableQuantity || "0"}</td>
              <td>
                ৳{item.supplierPrice ? item.supplierPrice.toFixed(2) : "0.00"}
              </td>
              <td>৳{item.amount ? item.amount.toFixed(2) : "0.00"}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="10" style={{ textAlign: "center" }}>
              No medicines available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  const subTotal =
    parsedItems.length > 0
      ? parsedItems.reduce((sum, item) => sum + (item.amount || 0), 0)
      : 0;

  const totalTaxAmount =
    parsedItems.length > 0
      ? parsedItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
      : 0;

  const summaryContent = (
    <>
      <div>
        <span>Total</span>
        <span>
          ৳{purchase.totalAmount ? purchase.totalAmount.toFixed(2) : "0.00"}
        </span>
      </div>
      <div>
        <span>Discount</span>
        <span>
          ৳{purchase.discount ? purchase.discount.toFixed(2) : "0.00"}
        </span>
      </div>
      <div>
        <span>Vat</span>
        <span>৳{totalTaxAmount ? totalTaxAmount.toFixed(2) : "0.00"}</span>
      </div>
      <div>
        <span>Net Amount</span>
        <span>
          ৳{purchase.netAmount ? purchase.netAmount.toFixed(2) : "0.00"}
        </span>
      </div>
    </>
  );

  const noteContent = purchase.paymentNote && (
    <p>
      <strong>Payment Note:</strong> {purchase.paymentNote}
    </p>
  );

  // Debug props passed to PrintLayout
  // console.log("PrintLayout props:", {
  //   billInfoContent,
  //   tableContent,
  //   summaryContent,
  //   noteContent,
  // });

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                {title}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrint}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiX />
              </button>
            </div>
          </div>

          <div className="px-4 print-hidden">
            {/* Invoice Info */}
            <div className="mt-4 sm:flex justify-between items-start mb-2">
              <div>
                {/* Bill No */}
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Invoice No</span>:{" "}
                  <span className="text-M-text-color">
                    {purchase.billNo || "N/A"}
                  </span>
                </p>
                {/* Supplier Name */}
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Supplier Name</span>:{" "}
                  <span className="text-M-text-color">
                    {purchase.supplier?.supplierName || "N/A"}
                  </span>
                </p>
               <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Authorized By</span>:{" "}
                  <span className="text-M-text-color">
                    {purchase.authorizedBy || "N/A"}
                  </span>
                </p>
              </div>
              {/* Payment Mode */}
              <div>
                {/* Purchase date */}
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Purchase Date:</span>
                  :{" "}
                  <span className="text-M-text-color">
                    {purchase.purchaseDate
                      ? format(new Date(purchase.purchaseDate), "dd/MM/yyyy")
                      : "N/A"}
                  </span>
                </p>
                {/* Payment Mode */}
                <p className="text-[15px] text-black mb-1">
                  <span className="w-[120px] inline-block">Payment Mode</span>:{" "}
                  <span className="text-M-text-color capitalize">
                    {purchase.paymentMode || "N/A"}
                  </span>
                </p>
                {/* Document */}
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">Document:</span>:{" "}
                  <span className="text-M-text-color">
                    {purchase.documentUrl ? (
                      <a
                        href={`https://api.muktihospital.com${purchase.documentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-M-primary-color hover:underline"
                      >
                        View
                      </a>
                    ) : (
                      "None"
                    )}
                  </span>
                </p>
              </div>
            </div>

            {/* Table */}
            <div className="gap-4">
              <div>
                <div className="overflow-x-auto">
                  <table className="min-w-[1440px] w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Medicine Name
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Category
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Batch No.
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Expiry Date
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Box Qty
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Quantity
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Available Quantity
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Supplier Price (৳)
                        </th>
                        <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Box Price (৳)
                        </th>
                        <th className="text-end uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                          Amount (৳)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedItems.length > 0 ? (
                        parsedItems.map((item, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.medicineName || "N/A"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.medicineCategory || "N/A"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.batchNo || "N/A"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.expiryDate
                                ? format(
                                    new Date(item.expiryDate),
                                    "dd-MM-yyyy"
                                  )
                                : "N/A"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.boxQty || "0"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.quantity || "0"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.availableQuantity || "0"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              ৳
                              {item.supplierPrice
                                ? item.supplierPrice.toFixed(2)
                                : "0.00"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              ৳
                              {item.boxPrice
                                ? item.boxPrice.toFixed(2)
                                : "0.00"}
                            </td>
                            <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              ৳{item.amount ? item.amount.toFixed(2) : "0.00"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-5">
                            No medicines available
                          </td>
                        </tr>
                      )}

                      {/* Calculation Area */}
                      {/* Sub Total */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3 pt-4"
                          colSpan={9}
                        >
                          <p>
                            <strong>Sub Total (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3">
                          <p>{subTotal ? subTotal.toFixed(2) : "0.00"}</p>
                        </td>
                      </tr>
                      {/* Vat */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3"
                          colSpan={9}
                        >
                          <p>
                            <strong>Vat (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3">
                          <p>
                            {totalTaxAmount
                              ? totalTaxAmount.toFixed(2)
                              : "0.00"}
                          </p>
                        </td>
                      </tr>
                      {/* Discount */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3"
                          colSpan={9}
                        >
                          <p>
                            <strong>Discount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3">
                          <p>
                            {purchase.discount
                              ? purchase.discount.toFixed(2)
                              : "0.00"}
                          </p>
                        </td>
                      </tr>
                      {/* Net Total */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-M-heading-color"
                          colSpan={9}
                        >
                          <strong className="py-1 px-3 text-sm">
                            Net Total (৳) :{" "}
                          </strong>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color">
                          <strong className="py-1 px-3 border-t border-M-text-color/20 block">
                            {purchase.netAmount
                              ? purchase.netAmount.toFixed(2)
                              : "0.00"}
                          </strong>
                        </td>
                      </tr>
                      {/* Return Amount */}
                      {purchase.returnAmount !== null && (
                        <tr>
                          <td
                            className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3"
                            colSpan={9}
                          >
                            <p>
                              <strong>Return Amount (৳) :</strong>
                            </p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3">
                            <p>{purchase.returnAmount.toFixed(2)}</p>
                          </td>
                        </tr>
                      )}
                      {/* Due Amount */}
                      {purchase.dueAmount !== null && (
                        <tr>
                          <td
                            className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3"
                            colSpan={9}
                          >
                            <p>
                              <strong>Due Amount (৳) :</strong>
                            </p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-1 px-3">
                            <p>{purchase.dueAmount.toFixed(2)}</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Payment Note */}

                {purchase.paymentNote && (
                  <p className="text-[15px] text-black mb-1 max-w-[600px] ">
                    <span className="inline-block mr-1 capitalize font-inter text-M-heading-color font-semibold">
                      Note:{" "}
                    </span>
                    <span className="text-M-text-color ">
                      {purchase.paymentNote}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <PrintLayout
        className="print-layout"
        billInfo={billInfoContent}
        footer={signatureFooter} 
        table={tableContent}
        summary={summaryContent}
        note={noteContent}
        title="Purchase Details"
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        ids={purchase.id}
        entityName="purchase"
        title="Are you sure?"
        message="Do you really want to delete this purchase?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default ViewPurchaseModal;
